import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ApiError, EmailSchema, apiGet, apiPost } from "./api";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; email: string }
  | { status: "anonymous" };

interface AuthContextValue {
  state: AuthState;
  issueLink: (email: string) => Promise<void>;
  verifyToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  async function refresh() {
    try {
      const me = await apiGet("/api/auth/me", EmailSchema);
      setState({ status: "authenticated", email: me.email });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: "anonymous" });
      } else {
        // network error → treat as anonymous so we don't hang the UI
        setState({ status: "anonymous" });
      }
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const value: AuthContextValue = {
    state,
    issueLink: async (email) => {
      await apiPost("/api/auth/issue", EmailSchema.partial(), { email });
    },
    verifyToken: async (token) => {
      const me = await apiPost("/api/auth/verify", EmailSchema, { token });
      setState({ status: "authenticated", email: me.email });
    },
    logout: async () => {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
      setState({ status: "anonymous" });
    },
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

function AppLoading() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        fontFamily: "var(--font-mono)",
        color: "var(--text-muted)",
        fontSize: 11,
        letterSpacing: 1.4,
        textTransform: "uppercase",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: "2px solid var(--border)",
          borderTopColor: "var(--accent-bright)",
          borderRadius: "50%",
          animation: "anglerfish-spin 0.9s linear infinite",
        }}
      />
      <span>Anglerfish Ops</span>
      <style>{`@keyframes anglerfish-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const loc = useLocation();
  if (state.status === "loading") return <AppLoading />;
  if (state.status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  return <>{children}</>;
}
